import debounce from 'lodash/debounce'
import { fetchExperienceCategories, fetchExperienceListFacets } from 'actions/ExperienceActions'
import FilterPanelCheckboxGroup, { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelCheckboxLoader from 'components/Common/FilterPanel/FilterPanelCheckboxLoader'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import DualSliderInput from 'components/Luxkit/Slider/DualSliderInput'
import { arrayToObject, groupBy, sortBy, sum, without } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import React, { useCallback, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import Group from 'components/utils/Group'

interface Props {
  placeId: string;
  facetState?: App.ExperienceListFacetsState[string];
  categories: Array<App.ExperienceItemCategory>;
}

function ExperienceSearchPageFilters(props: Props) {
  const { categories, facetState, placeId } = props
  const dispatch = useAppDispatch()
  const {
    searchFilters,
    setCategoryCodesSearchFilter,
    setPriceGteSearchFilter,
    setPriceLteSearchFilter,
  } = useExperiencesSearchFilters()

  useEffect(() => {
    dispatch(fetchExperienceListFacets(placeId))
    dispatch(fetchExperienceCategories())
  }, [dispatch, placeId])

  const onCategoryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const category = categories.find(cat => cat.id === parseInt(e.currentTarget.value))
    const selectedCategories = searchFilters.categoryCodes ?? []

    let nextCategories = [...selectedCategories]
    if (selectedCategories.includes(category.parentId)) {
      // parentID selected - at the moment, all items for the parent are selected
      // in this instance, we toggle the parent and this category off but ensure all others remain
      const otherChildCategories = categories.filter(cat => cat.parentId === category.parentId && cat.id !== category.id)
      nextCategories = otherChildCategories.map(cat => cat.id)
    } else if (selectedCategories.includes(category.id)) {
      // toggling off a single category
      nextCategories = without(nextCategories, category.id)
    } else {
      // toggling on a single category
      nextCategories.push(category.id)
    }

    setCategoryCodesSearchFilter(nextCategories)
  }, [categories, searchFilters.categoryCodes, setCategoryCodesSearchFilter])

  const categoryItems = useMemo(() => {
    const childCategoriesByParent = groupBy(
      categories,
      cat => cat.parentId,
    )
    const facetsById = arrayToObject(facetState?.facets?.category ?? [], facet => facet.categoryId)
    const topLevelCategories = categories.filter(cat => cat.level === 'parent')
    const selectedCategories = searchFilters.categoryCodes ?? []

    return topLevelCategories.map((category) => {
      const childCategories = sortBy(childCategoriesByParent.get(category.id) ?? [], cat => facetsById[cat.id]?.count ?? 0, 'desc')
      const total = sum(childCategories, cat => facetsById[cat.id]?.count ?? 0)

      return {
        category,
        count: total,
        items: childCategories.map((category): FilterPanelCheckItem => {
          const count = facetsById[category.id]?.count ?? 0
          return {
            label: <Group direction="horizontal" horizontalAlign="space-between">
              {category.name}
              <span>({count})</span>
            </Group>,
            hidden: count === 0,
            value: category.id,
            checked: selectedCategories.includes(category.id) || selectedCategories.includes(category.parentId),
          }
        }),
      }
    }).filter(val => val.count > 0)
  }, [categories, facetState?.facets?.category, searchFilters.categoryCodes])

  const slider = useMemo(() => {
    if (facetState?.facets?.price) {
      const { min: pmin, max: pmax } = facetState.facets.price
      const step = pmax <= 10000 ? 10 : 100
      const min = Math.floor(pmin / step) * step
      const max = Math.ceil(pmax / step) * step
      return { min, max, step }
    }
  }, [facetState])

  const onResetCategory = useCallback((item: any) => {
    const selectedCategories = searchFilters.categoryCodes ?? []
    const nextCategories = without(selectedCategories, item.category.id, ...item.items.map(i => i.value))
    setCategoryCodesSearchFilter(nextCategories)
  }, [searchFilters.categoryCodes, setCategoryCodesSearchFilter])

  const onPriceGteChange = useMemo(() => debounce((event: any) => {
    setPriceGteSearchFilter(parseInt(event.target.value))
  }, 500), [setPriceGteSearchFilter])

  const onPriceLteChange = useMemo(() => debounce((event: any) => {
    setPriceLteSearchFilter(parseInt(event.target.value))
  }, 500), [setPriceLteSearchFilter])

  return <VerticalSpacer gap={24}>
    {(!facetState || facetState.fetching) && <FilterPanelCheckboxLoader />}
    <FilterPanelFilterGroup
        title="Filter by Price"
      >
      { slider && <DualSliderInput
          format="currency"
          min={slider.min}
          max={slider.max}
          defaultStartValue={searchFilters.priceGte}
          defaultEndValue={searchFilters.priceLte}
          step={slider.step}
          onStartChange={onPriceGteChange}
          onEndChange={onPriceLteChange}
          variant="label"
        />}
    </FilterPanelFilterGroup>
    {categoryItems.map(item =>
      <FilterPanelFilterGroup
        key={item.category.id}
        title={item.category.name}
        canReset={item.items.some(item => item.checked)}
        onReset={() => onResetCategory(item)}
      >
        <FilterPanelCheckboxGroup
          name="categories"
          items={item.items}
          onChange={onCategoryChange}
          maxItems={5}
        />
      </FilterPanelFilterGroup>,
    )}
  </VerticalSpacer>
}

ExperienceSearchPageFilters.defaultProps = {
  categories: [],
  selectedCategories: [],
  onFilterChange: noop,
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>) {
  return {
    facetState: state.experience.experienceListFacets[ownProps.placeId],
    categories: state.experience.experienceCategories,
  }
}

export default connect(mapStateToProps)(ExperienceSearchPageFilters)
